/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { VivaExam_UserFeedbackChoice as VivaExamUserFeedBackChoice } from '__generated__/graphql-types';

import { Button, InlineNotification, Tag, TextField, Typography2 } from '@coursera/cds-core';
import { FlagIcon, ThumbsDownFilledIcon, ThumbsDownIcon, ThumbsUpFilledIcon, ThumbsUpIcon } from '@coursera/cds-icons';

import { FeedbackStatus } from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/lib/constants';

import _t from 'i18n!nls/academic-integrity';

const styles = {
  feedbackIcon: (isSelected: boolean) => css`
    color: ${isSelected ? 'var(--cds-color-interactive-primary)' : 'inherit'};
    cursor: pointer;

    &:hover {
      color: var(--cds-color-interactive-primary);
    }
  `,
  flagAndFeedbackRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-300);
  `,
  feedbackTextfield: css`
    margin-top: var(--cds-spacing-200);
    display: flex;
    flex-direction: column;
  `,
  submitFeedbackButton: css`
    display: flex;
    margin-top: var(--cds-spacing-200);
  `,
  feedbackText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  feedbackNotification: css`
    margin-top: var(--cds-spacing-200);
  `,
};

type IntegrityCheckFeedbackStatusProps = {
  vivaExamFlagRaised: boolean;
  userFeedbackSubmitted: boolean;
  feedbackChoice?: VivaExamUserFeedBackChoice;
  feedback: string;
  handleIconClick: (choice: VivaExamUserFeedBackChoice) => void;
  handleFeedbackChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSendFeedback: () => void;
};

const IntegrityCheckFeedbackStatus: React.FC<IntegrityCheckFeedbackStatusProps> = ({
  vivaExamFlagRaised,
  userFeedbackSubmitted,
  feedbackChoice,
  feedback,
  handleIconClick,
  handleFeedbackChange,
  handleSendFeedback,
}) => {
  if (userFeedbackSubmitted) {
    return (
      <InlineNotification severity="success" css={styles.feedbackNotification}>
        {_t('Integrity Check feedback submitted')}
      </InlineNotification>
    );
  }

  return (
    <React.Fragment>
      <div css={styles.flagAndFeedbackRow}>
        <Tag
          color={vivaExamFlagRaised ? 'error' : 'default'}
          icon={<FlagIcon size="medium" />}
          priority="secondary"
          variant="status"
        >
          {vivaExamFlagRaised ? _t('AI-generated integrity flag') : _t('No AI-generated integrity flag')}
        </Tag>

        <Typography2 variant="bodyPrimary" component="span" css={styles.feedbackText}>
          {_t('Is this flag status accurate?')}
        </Typography2>

        {feedbackChoice === FeedbackStatus.LIKE ? (
          <ThumbsUpFilledIcon
            role="button"
            onClick={() => handleIconClick(FeedbackStatus.LIKE)}
            size="medium"
            css={styles.feedbackIcon(true)}
          />
        ) : (
          <ThumbsUpIcon
            role="button"
            onClick={() => handleIconClick(FeedbackStatus.LIKE)}
            size="medium"
            css={styles.feedbackIcon(false)}
            data-testid="thumbs-up-icon"
          />
        )}

        {feedbackChoice === FeedbackStatus.DISLIKE ? (
          <ThumbsDownFilledIcon
            role="button"
            onClick={() => handleIconClick(FeedbackStatus.DISLIKE)}
            size="medium"
            css={styles.feedbackIcon(true)}
          />
        ) : (
          <ThumbsDownIcon
            role="button"
            onClick={() => handleIconClick(FeedbackStatus.DISLIKE)}
            size="medium"
            css={styles.feedbackIcon(false)}
            data-testid="thumbs-down-icon"
          />
        )}
      </div>
      {feedbackChoice && (
        <div>
          <div css={styles.feedbackTextfield}>
            <TextField
              optional
              label={_t('Share your feedback')}
              placeholder={_t('Add more details to help us improve Integrity Check accuracy...')}
              value={feedback}
              onChange={handleFeedbackChange}
              multiline
              minRows={1}
            />
          </div>
          <Button size="small" variant="secondary" onClick={handleSendFeedback} css={styles.submitFeedbackButton}>
            {_t('Submit Feedback')}
          </Button>
        </div>
      )}
    </React.Fragment>
  );
};

export default IntegrityCheckFeedbackStatus;
